import omit from 'lodash/omit';
import { useCallback, useRef } from 'react';

import { devWarning } from '@tanium/coreui-utils';
import { useControlledProp } from '@tanium/react-use-controlled-prop';

export const FILTER_BUILDER_KEY = 'filterBuilder';

export interface UpdateFilterArgs {
  /**
   * The unique dataKey of the filter being changed. This key is the same key used to persist the
   * value in the AdvancedFilter. This may also be used for accessibility.
   */
  dataKey: string;
  /**
   * Whatever output the AdvancedFilter produces.
   */
  value?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * The display name of the filter. This is used for displaying the pill in the AdvancedFilter header.
   */
  label?: string;
  /**
   * The string to display in the pill representing the value of the AdvancedFilterInput.
   */
  displayValue?: string;
  /**
   * If set to true, all other filter values will be omitted.
   * Defaults to false
   */
  replaceExisting?: boolean;
}
interface AdvancedFilterChangeEvent {
  value: UseAdvancedFiltersProps['value'];
  filterValues: { [key: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export type OnChangeEventHandler = (event: AdvancedFilterChangeEvent) => void;

export interface FilterValue {
  /**
   * The value of the AdvancedFilter. If undefined, it signifies that the filter should not be
   * included in the value of the AdvancedFilter.
   */
  value: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * Display properties for the pill in the AdvancedFilters header UX.
   */
  pill: {
    /**
     * Label given to the pill representing this filter.
     */
    label?: string;
    /**
     * String to represent the value of the filter when displayed in a pill.
     */
    displayValue: string;
  };
}

export interface UseAdvancedFiltersReturnValue {
  clear: () => void;
  value: { [key: string]: FilterValue };
  updateFilter: (args: UpdateFilterArgs) => void;
  pills: string[];
  drafts: { [key: string]: unknown };
}

export interface UseAdvancedFiltersProps {
  /**
   * The value for the AdvancedFilter component. Contains all filter values including pill values displayed
   * in the header.
   * Keys correspond to the `dataKey`s
   * assigned to AdvancedFilterInput components. Values are supplied directly by the AdvancedFilterInput.
   */
  value?: { [key: string]: FilterValue };
  /**
   * Value of the filters upon initialization. This has no effect if `value` is defined.
   */
  defaultValue?: UseAdvancedFiltersProps['value'];
  /**
   * Callback triggered when the AdvancedFilter's value changes. This callback makes two objects
   * accessible:
   * - the `filterValues` object, which holds the values of all the AdvancedFilterInputs in the
   *   AdvancedFilters;
   * - the `values` object, which in addition to the values of the AdvancedFilterInputs, holds the
   *   values of the pills in the header.
   *
   * If the AdvancedFilter is not controlled, the `filterValues` parameter of the callback will be
   * more succinct.
   */
  onChange?: OnChangeEventHandler;
}

export const validateValues = (values: UseAdvancedFiltersProps['value']) => {
  const keys = Object.keys(values || {});
  const filterBuilderExists = !!keys.find((k) => k === FILTER_BUILDER_KEY);
  devWarning(
    () => !filterBuilderExists || (filterBuilderExists && keys.length === 1),
    `Filter Builder should not be applied when other filters are set.`,
  );
};

const getFilterValues = (updatedValue: NonNullable<UseAdvancedFiltersProps['value']>) =>
  Object.keys(updatedValue).reduce((acc, key) => ({ ...acc, [key]: updatedValue[key].value }), {});

const useAdvancedFilters = ({
  value: valueProp,
  defaultValue = {},
  onChange,
}: UseAdvancedFiltersProps): UseAdvancedFiltersReturnValue => {
  const [value, setValue] = useControlledProp({
    defaultValue,
    componentName: 'AdvancedFilters',
    onUpdate: ({ value: updatedValue }) => {
      drafts.current = {};
      if (onChange) {
        const values = getFilterValues(updatedValue);
        onChange({ value: updatedValue, filterValues: values });
      }
    },
    propName: 'value',
    value: valueProp,
  });
  const drafts = useRef<{ [key: string]: unknown }>({});
  const getPills = useCallback(() => Object.keys(value).filter((key) => !!value[key].pill), [
    value,
  ]);

  const clear = useCallback(() => {
    setValue({});
  }, [setValue]);

  const updateFilter = useCallback(
    ({
      dataKey,
      value: updateValue,
      label,
      displayValue,
      replaceExisting = false,
    }: UpdateFilterArgs) => {
      const pill = { label, displayValue };
      if (updateValue === undefined) {
        setValue(omit(value, dataKey));
      } else {
        const newKeyValue = {
          [dataKey]: { pill, value: updateValue },
        };

        const newValue = {
          ...(replaceExisting ? {} : value),
          ...newKeyValue,
        };
        setValue(newValue as NonNullable<UseAdvancedFiltersProps['value']>);
      }
    },
    [value, setValue],
  );

  if (process.env.NODE_ENV !== 'production') {
    validateValues(value);
  }

  return {
    clear,
    value,
    updateFilter,
    pills: getPills(),
    drafts: drafts.current,
  };
};

export default useAdvancedFilters;
